import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ApiKey, ApiKeysService } from 'src/app/services/api-keys.service';
import { DesApiKeyLink } from 'src/app/services/machines.service';
import { ErrorService } from 'src/app/services/error.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-tenant-api-key',
  templateUrl: './tenant-api-key.component.html',
  styleUrl: './tenant-api-key.component.scss',
})
export class TenantApiKeyComponent implements OnChanges {
  @Input() tenantId?: string;
  @Input() desApiKeyLink?: DesApiKeyLink;
  @Input() desApiKeyLinkLoading = false;
  @Input() desApiKeyLinkLoadError = '';

  @ViewChild('deleteDialog')
  deleteDialog!: ElementRef<HTMLDialogElement>;

  apiKey: ApiKey | null = null;
  newApiKey = '';
  loading = false;
  creating = false;
  deleting = false;
  error = '';
  deleteError = '';
  shown = false;
  hiddenKeyString = '••••••••-••••-••••-••••-••••••••••••';

  readonly notLinkedMessage = 'API Key is not linked to DES Credit record';
  readonly multipleLinksWarning = 'Warning: multiple DES Key links exist';

  constructor(
    private apiKeysService: ApiKeysService,
    private errorService: ErrorService,
    private toastService: ToastService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tenantId'] && this.tenantId) {
      this.deleting = false;
      this.deleteError = '';
      this.load();
    }
  }

  load() {
    if (!this.tenantId) {
      return;
    }

    this.loading = true;
    this.error = '';
    this.apiKeysService.getApiKeyByTenantId(this.tenantId).subscribe({
      next: (res) => {
        this.loading = false;
        this.apiKey = res ?? null;
        if (!this.apiKey) {
          this.shown = false;
        } else {
          this.newApiKey = '';
        }
      },
      error: (err) => {
        this.loading = false;
        this.error = this.errorService.getErrorMessage(err);
      },
    });
  }

  get canAdd(): boolean {
    return (
      !!this.tenantId &&
      !this.creating &&
      !this.apiKey &&
      this.newApiKey.trim().length > 0
    );
  }

  addApiKey() {
    if (!this.canAdd || !this.tenantId) {
      return;
    }

    this.creating = true;
    this.error = '';
    this.apiKeysService
      .createApiKey({
        tenant_id: this.tenantId,
        api_key: this.newApiKey.trim(),
      })
      .subscribe({
        next: (res) => {
          this.creating = false;
          this.apiKey = res;
          this.newApiKey = '';
          this.shown = true;
          this.toastService.addToast('info', 'API key created');
        },
        error: (err) => {
          this.creating = false;
          this.error = this.errorService.getErrorMessage(err);
        },
      });
  }

  copyKey(event?: Event) {
    event?.stopPropagation();
    if (!this.apiKey?.api_key) {
      return;
    }
    navigator.clipboard.writeText(this.apiKey.api_key).then(() => {
      this.toastService.addToast('info', 'API key copied to clipboard');
    });
  }

  toggleShown() {
    this.shown = !this.shown;
  }

  get showMultipleLinks(): boolean {
    if (!this.apiKey || this.loading || this.error) {
      return false;
    }
    if (this.desApiKeyLinkLoading || this.desApiKeyLinkLoadError) {
      return false;
    }
    if (this.desApiKeyLink?.lookup_error) {
      return false;
    }
    return this.desApiKeyLink?.multiple_link_records === true;
  }

  get showNotLinked(): boolean {
    if (!this.apiKey || this.loading || this.error) {
      return false;
    }
    if (this.desApiKeyLinkLoading || this.desApiKeyLinkLoadError) {
      return false;
    }
    if (this.desApiKeyLink?.lookup_error) {
      return false;
    }
    if (this.showMultipleLinks) {
      return false;
    }
    return this.desApiKeyLink?.has_link_record !== true;
  }

  showDeleteDialog() {
    this.deleteError = '';
    this.deleting = false;
    this.deleteDialog.nativeElement.showModal();
  }

  closeDeleteDialog() {
    this.deleteDialog.nativeElement.close();
    this.deleteError = '';
    this.deleting = false;
  }

  deleteApiKey() {
    if (!this.apiKey?.id || this.deleting) {
      return;
    }

    const targetTenantId = this.tenantId;
    const targetKeyId = this.apiKey.id;
    const isCurrentTarget = () =>
      this.tenantId === targetTenantId && this.apiKey?.id === targetKeyId;

    this.deleting = true;
    this.deleteError = '';
    this.apiKeysService.deleteApiKey(targetKeyId).subscribe({
      next: () => {
        this.toastService.addToast('info', 'API key deleted');
        if (!isCurrentTarget()) {
          return;
        }
        this.deleting = false;
        this.apiKey = null;
        this.newApiKey = '';
        this.shown = false;
        this.closeDeleteDialog();
      },
      error: (err) => {
        if (!isCurrentTarget()) {
          return;
        }
        this.deleting = false;
        this.deleteError = this.errorService.getErrorMessage(err);
      },
    });
  }
}
